import React, { useEffect, useState } from 'react'
import './index.scss'
import { ICONS } from '@Const/common'
import AlternativeTitleCard from '@Components/Cards/WorkDetailCards/AlternativeTitleCard'
import RecordingCard from '@Components/Cards/WorkDetailCards/RecordingCard'
import WriterCardFull from '@Components/Cards/WorkDetailCards/WriterCards/WriterCardFull'
import OriginalPublisherCardFull from '@Components/Cards/WorkDetailCards/OriginalPublisherCards/OriginalPublisherCardFull'
import AdminPublisherCardFull from '@Components/Cards/WorkDetailCards/AdminPublisherCards/AdminPublisherCardFull'
import { publishersCounter } from '@App/helpers/publishers-counter'
import { TOOLTIP_TEXT } from '@Const/tooltip-text'
import TagComponent from '../Tag'
import { Recording, RecordingList } from '@Entities/matching/recording-group-details'
import { getDspRecordingsForSongCode } from '@App/api/routes/recording'
import { AlternativeTitleModel } from '@Entities/work/alternative-title-model'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { WriterModel } from '@Entities/work/writer-model'

interface ExtendInfoToWorkItemProps {
    writers: WriterModel[]
    alternativeTitles: AlternativeTitleModel[]
    originalPublishers: OriginalPublisherModel[]
    matchedRecordings: RecordingList | null
    holdTypeId?: number | undefined
    iswc?: string | null
    songCode?: string
}

const ExtendInfoToWorkItem = (props: ExtendInfoToWorkItemProps) => {
    const { writers, alternativeTitles, originalPublishers, matchedRecordings, holdTypeId, iswc, songCode } = props
    const [completeMatchedRecordings, setCompleteMatchedRecordings] = useState<Recording[]>()
    const allPublishersCount = publishersCounter(originalPublishers)

    const holdIds = [21, 61, 62, 63, 64, 101, 102, 103, 104, 105, 106, 107]
    const onHold = holdTypeId ? holdIds.includes(holdTypeId) : false

    useEffect(() => {
        getDspRecordingsForSongCode(songCode ?? '', { limit: 10, page: 1 }).then((axiosResult) => {
            setCompleteMatchedRecordings(axiosResult.data.recordings)
        })
    }, [])

    return (
        <div className="extend-info-work-item">
            <div className="review-work-detail">
                <div className="review-entity-head">
                    <div>
                        <div className="entity-img">
                            <img src={ICONS.workDetails} alt="work details" />
                        </div>
                        <h5>Work Details</h5>
                        {onHold && <TagComponent type="dispute" name="On Hold: Has Rights in Dispute" />}
                    </div>
                </div>

                {iswc ? (
                    <div className="work-detail-info">
                        <p className="small-2">
                            <span>ISWC:&nbsp;</span>
                            <strong>{iswc}</strong>
                        </p>
                    </div>
                ) : null}

                <div className="review-cards">
                    <p className="small bold">Alternative titles</p>
                    {alternativeTitles.length
                        ? alternativeTitles.map((data: AlternativeTitleModel, index: number) => (
                              <div className="review-alternative-title-card" key={data.id ? data.id : index}>
                                  <AlternativeTitleCard data={data} />
                              </div>
                          ))
                        : null}
                </div>
            </div>

            <div className="review-writers">
                <div className="review-entity-head">
                    <div>
                        <div className="entity-img">
                            <img src={ICONS.writers} alt="writers" />
                        </div>
                        <h5>Writers ({writers.length})</h5>
                        <div className="helper" title={TOOLTIP_TEXT.extendInfoToWorkItem.writers}>
                            <img src={ICONS.help2} alt="helper" />
                        </div>
                    </div>
                </div>
                <div className="review-cards">
                    {writers.length
                        ? writers.map((data: WriterModel, index: number) => (
                              <div className="review-writers-card" key={data.id ? data.id : index}>
                                  <WriterCardFull data={data} />
                              </div>
                          ))
                        : null}
                </div>
            </div>

            <div className="review-publishers">
                <div className="review-entity-head">
                    <div>
                        <div className="entity-img">
                            <img src={ICONS.publishers} alt="publishers" />
                        </div>
                        <h5>Publishers ({allPublishersCount})</h5>
                    </div>
                </div>

                {originalPublishers.length
                    ? originalPublishers.map((op: OriginalPublisherModel, index: number) => (
                          <React.Fragment key={op.id ? op.id : index}>
                              <div className="review-cards">
                                  <p className="small bold">Publisher</p>
                                  <div className="review-op-card">
                                      <OriginalPublisherCardFull data={op} />
                                  </div>
                              </div>
                              {op.administratorPublishers.length ? (
                                  <div className="review-cards">
                                      <p className="small bold">Administrators ({op.administratorPublishers.length})</p>
                                      {op.administratorPublishers.map((ap: PublisherModel, apIndex: number) => (
                                          <div className="review-ap-card" key={ap.id ? ap.id : apIndex}>
                                              <AdminPublisherCardFull data={ap} />
                                          </div>
                                      ))}
                                  </div>
                              ) : null}
                          </React.Fragment>
                      ))
                    : null}
            </div>

            <div className="review-recordings">
                <div className="review-entity-head">
                    <div>
                        <div className="entity-img">
                            <img src={ICONS.recordings} alt="recordings" />
                        </div>
                        <h5>Recordings ({matchedRecordings?.count ?? 0})</h5>
                    </div>
                </div>
                <div className="review-cards">
                    {completeMatchedRecordings?.map((data: Recording, index: number) => (
                        <div className="review-recordings-card" key={data.id ? data.id : index}>
                            <RecordingCard data={data} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default ExtendInfoToWorkItem
